import React, { useMemo } from 'react';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { Button, Result } from 'antd';
import chatttSDK from 'services/chattt';
import { tolgee } from 'services/i18n/tolgee';
import { logger } from 'services/logger';

/**
 * 替换渲染函数
 */
export interface FallbackFunction {
  (error: Error, reset: () => void): React.ReactElement;
}

/**
 * 错误边界属性
 */
export interface ErrorBoundaryProps {
  /**
   * 抛错后替换渲染节点
   */
  fallback: FallbackFunction | React.ReactElement;
  componentName?: string;
  children: React.ReactNode;
}

const ErrorBoundary = ({ children, componentName, fallback }: ErrorBoundaryProps) => {
  const fallbackFn = useMemo<React.ComponentProps<typeof SentryErrorBoundary>['fallback']>(() => {
    if (typeof fallback === 'function') {
      return ({ error, resetError }) => fallback(error, resetError);
    }
    return fallback;
  }, [fallback]);
  return (
    <SentryErrorBoundary
      onError={(error: any, componentStack) => {
        const name = componentName === 'chat' ? 'ChatCrash' : 'Crash';
        logger.info(`[${name}] ${error?.message}`, {
          stack: componentStack || '',
          component: componentName || '',
        });
      }}
      fallback={fallbackFn}
    >
      {children}
    </SentryErrorBoundary>
  );
};

export const ErrorResult = ({ onClick }: { onClick?: () => void }) => {
  return (
    <Result
      status="500"
      title={tolgee.t('app_error_title')}
      subTitle={tolgee.t('app_error_subTitle')}
      extra={
        <Button
          type="primary"
          onClick={() =>
            onClick
              ? onClick()
              : chatttSDK?.app?.reload
              ? chatttSDK.app.reload()
              : window.location.reload()
          }
        >
          {tolgee.t('app_reload_now')}
        </Button>
      }
    />
  );
};

export default ErrorBoundary;
